/* The document pane's rules, kept apart from its rendering.
 *
 * Which page is showing, whether the quote can be highlighted on it, and what
 * the pane is allowed to say when it cannot, are decisions about data. There is
 * no browser test runner in this repository — ARCHITECTURE.md records why — so
 * they are pure functions here and tools/document-pane-check.tsx asserts them.
 *
 * The rule the whole module exists to enforce: a highlight is drawn only where
 * a stored span slices the finding's own words back out of the page text. Not
 * where a search nearly matched, not where a fuzzy comparison was close enough.
 * The pane's value is that a reviewer can trust the mark, and a mark that was
 * sometimes approximate would be worth less than no mark at all. */

import type { ExtractedPages, Flag, QuoteOffsets } from '../../types';

/* ==========================================================================
   Pages
   ========================================================================== */

/** A page number inside the document, whatever was asked for. */
export function clampPage(page: number, pageCount: number): number {
  if (pageCount <= 0) return 1;
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.trunc(page), 1), pageCount);
}

/**
 * What the jump-to-page box means. Null for anything that is not a page in this
 * document — an empty box, a word, a number past the end — so the box can
 * refuse rather than silently jump somewhere near.
 */
export function parsePageInput(raw: string, pageCount: number): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const page = Number(trimmed);
  if (page < 1 || page > pageCount) return null;
  return page;
}

export function textOfPage(pages: ExtractedPages | null, page: number): string {
  return pages?.pages.find((p) => p.number === page)?.text ?? '';
}

/* ==========================================================================
   The highlight
   ========================================================================== */

export interface Segments {
  before: string;
  quote: string;
  after: string;
}

/**
 * The page text split around the span, or null when the span cannot be
 * trusted: outside the text, empty, or inverted. A caller that gets null draws
 * no highlight.
 */
export function splitAt(text: string, offsets: QuoteOffsets): Segments | null {
  const { start, end } = offsets;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
  if (start < 0 || end > text.length || end <= start) return null;
  return {
    before: text.slice(0, start),
    quote: text.slice(start, end),
    after: text.slice(end),
  };
}

/**
 * Does this span actually hold the finding's quote?
 *
 * Compared with whitespace collapsed, because a PDF text layer breaks lines
 * where a quote does not — the same tolerance the quote gate already applies,
 * and no more. Every other character has to agree. A span that fails this is
 * treated as unlocated, which is why a stale offset shows the honest notice
 * instead of highlighting the wrong sentence.
 */
export function spanHoldsQuote(segments: Segments, quote: string | null): boolean {
  if (quote === null) return false;
  const fold = (value: string) => value.replace(/\s+/g, ' ').trim();
  return fold(segments.quote) === fold(quote) && fold(quote).length > 0;
}

/* ==========================================================================
   What the pane is showing
   ========================================================================== */

/**
 * Why no highlight is drawn on the page the finding cites.
 *
 * 'no-offsets'  the check never located the span
 * 'empty-page'  the page has no extracted text to highlight in
 * 'not-found'   there is a span, and it does not hold the quote
 *
 * All three say the same thing to the reviewer, because from where they sit
 * they are the same thing: Ceiling cannot show them the words on the page. The
 * distinction is kept so the state can be asserted, not so it can be explained.
 */
export type UnlocatedReason = 'no-offsets' | 'empty-page' | 'not-found';

export type DocumentView =
  /** Nothing is selected. */
  | { kind: 'no-finding' }
  /** The finding is about something the plan does not say. */
  | { kind: 'no-page' }
  /** The page text has been asked for and has not arrived. */
  | { kind: 'loading' }
  /** There is no stored page text for this document at all. */
  | { kind: 'unavailable' }
  | {
      kind: 'page';
      page: number;
      pageCount: number;
      /** The page's own text. Empty where the parse got nothing from it. */
      text: string;
      /** Set only when the quote is genuinely on this page. */
      segments: Segments | null;
      /** Set when the quote should have been on this page and was not. */
      unlocated: UnlocatedReason | null;
      /** Whether this is the page the selected finding cites. */
      onCitedPage: boolean;
      /** The page the selected finding cites, when it cites one. */
      citedPage: number | null;
    };

interface ViewInput {
  finding: Flag | null;
  pages: ExtractedPages | null;
  /** The page the reviewer navigated to, or null to follow the finding. */
  page: number | null;
  /** True while the page text is being read. */
  loading: boolean;
}

/**
 * Everything the pane renders, decided once.
 *
 * A finding that cites no page is answered before the page text is consulted:
 * "not tied to a single page" is a fact about the finding, and waiting on a
 * database to say it would be a spinner with a known answer behind it.
 */
export function documentView({
  finding,
  pages,
  page,
  loading,
}: ViewInput): DocumentView {
  if (!finding) return { kind: 'no-finding' };
  // Nowhere to go, unless the reviewer has already navigated somewhere.
  if (finding.page === null && page === null) return { kind: 'no-page' };
  if (loading) return { kind: 'loading' };
  if (!pages || pages.pages.length === 0) return { kind: 'unavailable' };

  const pageCount = pages.pageCount || pages.pages.length;
  const citedPage = finding.page === null ? null : clampPage(finding.page, pageCount);
  const current = clampPage(page ?? citedPage ?? 1, pageCount);
  const text = textOfPage(pages, current);
  const onCitedPage = citedPage !== null && current === citedPage;

  const base = {
    kind: 'page' as const,
    page: current,
    pageCount,
    text,
    onCitedPage,
    citedPage,
  };

  // The quote belongs to the page the finding cites. On any other page there is
  // nothing to highlight and nothing to apologise for, so no notice is shown —
  // the reviewer is simply reading elsewhere in the document.
  if (!onCitedPage) return { ...base, segments: null, unlocated: null };

  if (text.trim().length === 0) {
    return { ...base, segments: null, unlocated: 'empty-page' };
  }
  if (!finding.quoteOffsets) {
    return { ...base, segments: null, unlocated: 'no-offsets' };
  }
  if (finding.quoteOffsets.page !== current) {
    return { ...base, segments: null, unlocated: 'not-found' };
  }

  const segments = splitAt(text, finding.quoteOffsets);
  if (!segments || !spanHoldsQuote(segments, finding.quote)) {
    return { ...base, segments: null, unlocated: 'not-found' };
  }

  return { ...base, segments, unlocated: null };
}

/* ==========================================================================
   What the pane says
   ========================================================================== */

/** "Page 6 of 35". The header, and the only place the page count is stated. */
export function pageLabel(page: number, pageCount: number): string {
  return `Page ${page} of ${pageCount}`;
}

/**
 * The one thing the pane says when it cannot show the span, whichever of the
 * three ways it failed. Names the page so a reviewer can open their own copy,
 * and does not dress the failure up as anything else.
 */
export function unlocatedNotice(page: number): string {
  return `Ceiling couldn’t locate this quote on the page it extracted. The quote below is what it read; check page ${page} in your own copy.`;
}

/**
 * What aria-live announces when the pane moves. Assistive technology gets the
 * same facts the eye gets: which page, where in the plan it is, and whether the
 * quote was found on it.
 *
 * The finding's own location is included so that moving between two findings
 * that happen to share a page still changes the text. A live region that
 * re-renders identical words announces nothing, and a reviewer stepping through
 * a list would be told about the first of them and then nothing at all.
 */
export function announcementFor(
  view: DocumentView,
  finding: Flag | null = null,
): string {
  const where = finding?.sectionRef?.trim();

  switch (view.kind) {
    case 'no-finding':
      return '';
    case 'no-page':
      return 'This finding isn’t tied to a single page.';
    case 'loading':
      return 'Opening the document.';
    case 'unavailable':
      return 'The page text for this document isn’t in this browser.';
    case 'page': {
      const head = where
        ? `${pageLabel(view.page, view.pageCount)}, ${where}.`
        : `${pageLabel(view.page, view.pageCount)}.`;
      if (view.segments) return `${head} The quoted passage is highlighted.`;
      if (view.unlocated) {
        return `${head} The quoted passage could not be located on this page.`;
      }
      return head;
    }
    default:
      return '';
  }
}
